import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

export interface OcrClassificationStat {
  classification: string;
  accuracy: number;
  totalFields: number;
  totalCorrections: number;
  docsProcessed: number;
}

export type OcrAccuracyTier = "hot" | "cold";
export type OcrAccuracyBucket = "day" | "week" | "month";

export interface OcrAccuracyTrendPoint {
  date: string;
  accuracy: number;
  totalFields: number;
  totalCorrections: number;
}

export interface OcrAccuracyData {
  overall: {
    accuracy: number;
    totalFields: number;
    totalCorrections: number;
    docsProcessed: number;
  };
  byClassification: OcrClassificationStat[];
  trend?: OcrAccuracyTrendPoint[];
  tier?: OcrAccuracyTier;
  bucket?: OcrAccuracyBucket;
}

export function useOcrAccuracy(
  jobTypeId?: string,
  from?: string,
  to?: string,
  bucket?: OcrAccuracyBucket,
) {
  const [data, setData] = useState<OcrAccuracyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (jobTypeId) params.set("jobTypeId", jobTypeId);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      if (bucket) params.set("bucket", bucket);
      const query = params.toString() ? `?${params.toString()}` : "";
      const result = await api.get<OcrAccuracyData>(`/analytics/ocr-accuracy${query}`);
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load OCR accuracy data");
    } finally {
      setIsLoading(false);
    }
  }, [jobTypeId, from, to, bucket]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
